import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { BodyRegular, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import {
  createOrUpdateOffice,
  EditableAreaEntity,
  getOffice,
  MockEditableAreaEntity,
  OfficeEntity,
  updateSingleOfficeEntity
} from '@features/desks/services/AdvanceHotDeskingService';
import { getOfficeLayout } from '@features/desks/services/DeskBooking';
import { getParameterByName } from '@shared/utils/UrlUtils';
import {
  createDesk,
  deleteDeskRequest,
  Desk,
  EditableDesk,
  getDesksInOffice,
  updateDesk
} from '@features/desks/services/DeskBookingService';
import { EditableDeskSpot } from "./EditableDeskSpot";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import TextField from '@shared/ui/atoms/TextField';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  loadMeetingRooms,
  selectAllMeetingRooms
} from '@features/settings/pages/meeting-rooms/meeting-rooms-settings.duck';
import { getFloorPlanUrl } from "@features/whereabouts/pages/company-movements/components/OfficeFloorMap";
import { MeetingRoom } from "@features/meeting-rooms/pages/models/MeetingRooms";
import { updateMeetingRoomPosition, updateMeetingRoomSize } from '@features/meeting-rooms/MeetingRoomsService';
import { EditableMeetingRoomSpot } from "./EditableMeetingRoomSpot";
import { EditableAreaSpot } from "./EditableAreaSpot";
import { Prompt } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { selectCurrentUser } from '@shared/store/auth.duck';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import AccessRequired from '@features/settings/pages/components/AccessRequired';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import {
  fetchOfficeEntities,
  selectAllOfficeEntities
} from "@features/desks/store/advanceHotDeskingSetup.duck";
import { officeToCreateOfficeRequest } from "@shared/utils/OfficeHelper";

interface EditableMeetingRoom extends MeetingRoom {
  moved?: boolean;
  resized?: boolean;
}

const DEFAULT_AREA_RECT = {areaX: 150, areaY: 150, areaWidth: 200, areaHeight: 150};

// Keyboard nudge/rotate amounts for the selected desk. Deliberately fine — holding the key repeats.
const KEYBOARD_MOVE_STEP = 1;
const KEYBOARD_ROTATE_STEP = 1;

const PORTRAIT_DESK_PRESETS = [
  {width: 48, height: 96, labelKey: 'floor-plan-editor.desk-portrait'},
  {width: 48, height: 120, labelKey: 'floor-plan-editor.desk-portrait'},
  {width: 82, height: 123, labelKey: 'floor-plan-editor.desk-portrait'},
  {width: 92, height: 92, labelKey: 'floor-plan-editor.desk-portrait'},
  {width: 163, height: 161, labelKey: 'floor-plan-editor.corner-desk-portrait'},
];

const LANDSCAPE_DESK_PRESETS = [
  {width: 96, height: 48, labelKey: 'floor-plan-editor.desk-landscape'},
  {width: 120, height: 48, labelKey: 'floor-plan-editor.desk-landscape'},
  {width: 123, height: 82, labelKey: 'floor-plan-editor.desk-landscape'},
  {width: 98, height: 82, labelKey: 'floor-plan-editor.desk-landscape'},
  {width: 161, height: 163, labelKey: 'floor-plan-editor.corner-desk-landscape'},
];

export function FloorPlanEditPage(props: Props) {
  const dispatch = useDispatch()
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const canEdit = isCompanyAdminUser(currentUser);

  const [loading, setLoading] = useState<any>();
  const [lockedDesk, setLockedDesk] = useState<EditableDesk | undefined>();
  const [selectedDesk, setSelectedDesk] = useState<EditableDesk | undefined>();
  const [renameDeskEnabled, setRenameDeskEnabled] = useState<boolean>(false);
  const [editedDeskName, setEditedDeskName] = useState('');
  const [plan, setPlan] = useState<any>();
  const [office, setOffice] = useState<OfficeEntity | undefined>(undefined);
  const [editableDesks, setEditableDesks] = useState<EditableDesk[]>([]);
  const mapRef = useRef<any>(null);
  const lastMousePos = useRef({x: 0, y: 0});

  const allMeetingRooms = useSelector(selectAllMeetingRooms);
  const [selectedMeetingRoom, setSelectedMeetingRoom] = useState<EditableMeetingRoom | undefined>(undefined);
  const [editableMeetingRooms, setEditableMeetingRooms] = useState<EditableMeetingRoom[]>([])

  const allOffices = useSelector(selectAllOfficeEntities);
  const [draggingArea, setDraggingArea] = useState<EditableAreaEntity | undefined>(undefined);
  const [editableAreas, setEditableAreas] = useState<EditableAreaEntity[]>([]);

  const officeIdParam = useMemo(() => getParameterByName('officeId', window.location.href), []);
  const hasPlan = office?.officeFloorPlan || office?.officePlan;

  const childAreas = useMemo(() => {
    return allOffices.filter((o: OfficeEntity) => o.area && o.parentId === office?.id);
  }, [allOffices, office?.id]);

  const hasUnsavedChanges = useMemo(() => {
    return editableDesks.some(d => d.editted) ||
      editableMeetingRooms.some(r => r.moved || r.resized) ||
      editableAreas.some(a => a.dirty);
  }, [editableDesks, editableMeetingRooms, editableAreas]);

  useEffect(() => {
    if (office && office.floorPlanEnabled) {
      if (office.officeFloorPlan) {

      } else if (office.officePlan) {
        getOfficeLayout(office.officePlan).then(setPlan);
      }
    }
  }, [office]);

  useEffect(() => {
    if (!canEdit) return;
    dispatch(loadMeetingRooms())
    dispatch(fetchOfficeEntities())
  }, [dispatch, canEdit]);

  useEffect(() => {
    if (!canEdit) return;
    if (officeIdParam) {
      getOffice(parseInt(officeIdParam)).then(setOffice);
    }
  }, [canEdit, officeIdParam]);

  useEffect(() => {
    const rooms: EditableMeetingRoom[] = allMeetingRooms.filter(mr => mr.officeId === office?.id)
      .map(room => ({
        ...room,
        x: room.x ?? 100,
        y: room.y ?? 100,
        width: room.width ?? 0,
        height: room.height ?? 0,
        rotate: 0,
      }))
    setEditableMeetingRooms(prev => rooms.map(room => {
      const existing = prev.find(p => p.id === room.id);
      return (existing?.moved || existing?.resized) ? existing : room;
    }));
  }, [allMeetingRooms, office?.id]);

  useEffect(() => {
    setEditableAreas(prev => childAreas.map((area: OfficeEntity) => {
      const existing = prev.find(p => p.id === area.id);
      if (existing?.dirty) return existing;
      return {
        ...area,
        areaX: area.areaX ?? DEFAULT_AREA_RECT.areaX,
        areaY: area.areaY ?? DEFAULT_AREA_RECT.areaY,
        areaWidth: area.areaWidth ?? DEFAULT_AREA_RECT.areaWidth,
        areaHeight: area.areaHeight ?? DEFAULT_AREA_RECT.areaHeight,
        dirty: false,
      };
    }));
  }, [childAreas]);

  useEffect(() => {
    const findDeskInPlan = (desk: Desk): any | undefined => {
      try {
        return plan?.desks.find((d: any) => d.deskId === desk.id) as any | undefined;
      } catch {
        return;
      }
    }

    if (office && hasPlan) {
      getDesksInOffice(office.id).then((desks: Desk[]) => {
        const desksWithDefaults: EditableDesk[] = desks.map((d: Desk)=> {
          const deskFromPlan = findDeskInPlan(d);
          return {
            ...d,
            x: d?.x || deskFromPlan?.x || 450,
            y: d?.y || deskFromPlan?.y || 250,
            width: d?.width || deskFromPlan?.width || 48,
            height: d?.height || deskFromPlan?.height || 96,
            rotation: d?.rotation || deskFromPlan?.rotate || 0,
            editted: (!d.x && !!deskFromPlan),
          }
        })
        setEditableDesks(desksWithDefaults);
      });
    }
  }, [office, hasPlan, plan])

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // WASD nudges the selected desk, QE rotates it, C clones it. Re-registered whenever the
  // selection or the desk list changes so the handler never acts on a stale desk.
  useEffect(() => {
    if (!selectedDesk || renameDeskEnabled) return;

    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      let dx = 0, dy = 0, degrees = 0;
      switch (e.key.toLowerCase()) {
        case 'w': dy = -KEYBOARD_MOVE_STEP; break;
        case 's': dy = KEYBOARD_MOVE_STEP; break;
        case 'a': dx = -KEYBOARD_MOVE_STEP; break;
        case 'd': dx = KEYBOARD_MOVE_STEP; break;
        case 'q': degrees = -KEYBOARD_ROTATE_STEP; break;
        case 'e': degrees = KEYBOARD_ROTATE_STEP; break;
        case 'c':
          e.preventDefault();
          // Cloning hits the backend, so ignore auto-repeat rather than spawning a desk per tick
          if (!e.repeat && !loading) {
            cloneDesk();
          }
          return;
        default: return;
      }

      e.preventDefault();
      if (degrees !== 0) {
        rotateDesk(degrees);
      } else {
        moveDesk(dx, dy);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDesk, renameDeskEnabled, editableDesks, loading]);

  const mapScale = () => mapRef?.current?.instance?.transformState?.scale ?? 1;

  const onEditDeskClick = (e: any, desk: EditableDesk) => {
    setSelectedDesk(desk);
  }

  const onDeskLock = (desk: EditableDesk) => {
    setLockedDesk(desk);
    setSelectedDesk(undefined);
  }

  const onDeskRelease = (desk: EditableDesk) => {
    setLockedDesk(undefined);
  }

  const clearSelection = () => {
    setLockedDesk(undefined);
  }

  const addDesk = async () => {
    if (editableDesks.length === 0) {
      dispatch(failureNotification(t('floor-plan-editor.no-desks-warning')));
      return;
    }
    setLoading(true);
    try {
      clearSelection();
      const createdDesk = await createDesk({
          ...editableDesks[0],
          label: `Desk ${editableDesks.length+1}`,
          id: 0,
          active: true,
          x: 250,
          y: 250,
          width: 48,
          height: 96,
          rotation: 0,
        });

      setEditableDesks([
        ...editableDesks,
        createdDesk,
      ])
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.save-failed')));
    } finally {
      setLoading(false);
    }
  }

  // Copies the geometry of the selected desk, but not its team/user/workgroup allocations —
  // the clone lands exactly on top of the original, so it becomes the selected desk and can be dragged straight off.
  const cloneDesk = async () => {
    if (!selectedDesk) return;
    setLoading(true);
    try {
      clearSelection();
      const deskToCreate: EditableDesk = {
        ...selectedDesk,
        label: `Desk ${editableDesks.length + 1}`,
        id: 0,
        active: true,
        x: selectedDesk.x,
        y: selectedDesk.y,
        width: selectedDesk.width,
        height: selectedDesk.height,
        rotation: selectedDesk.rotation ?? 0,
        teamIds: [],
        users: [],
        workgroupIds: [],
        editted: false,
      };
      const createdDesk = await createDesk(deskToCreate);
      // Fall back to what we asked for if the response doesn't echo the geometry back
      const clonedDesk: EditableDesk = {
        ...deskToCreate,
        ...createdDesk,
        x: createdDesk.x ?? deskToCreate.x,
        y: createdDesk.y ?? deskToCreate.y,
        width: createdDesk.width ?? deskToCreate.width,
        height: createdDesk.height ?? deskToCreate.height,
        rotation: createdDesk.rotation ?? deskToCreate.rotation,
      };
      setEditableDesks([...editableDesks, clonedDesk]);
      setSelectedDesk(clonedDesk);
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.save-failed')));
    } finally {
      setLoading(false);
    }
  }

  const addArea = async () => {
    if (!office || !currentUser) return;
    setLoading(true);
    try {
      const created = await createOrUpdateOffice(officeToCreateOfficeRequest({
        ...MockEditableAreaEntity,
        label: t('floor-plan-editor.new-area'),
        parentId: office.id,
        companyId: currentUser.companyEntity.id,
      } as any as OfficeEntity));
      const withRect = {...created, ...DEFAULT_AREA_RECT};
      await updateSingleOfficeEntity(withRect);
      dispatch(fetchOfficeEntities());
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.area-create-failed')));
    } finally {
      setLoading(false);
    }
  }

  const changeDeskSize = (width: number, height: number) => {
    if (selectedDesk) {
      const updatedDesk = {
        ...selectedDesk,
        editted: true,
        height: height,
        width: width,
      }
      setSelectedDesk(updatedDesk);
      setEditableDesks(prev => prev.map(d => d.id === updatedDesk.id ? updatedDesk : d));
    }
  }

  const moveDesk = (dx: number, dy: number) => {
    if (selectedDesk) {
      const updatedDesk = {
        ...selectedDesk,
        x: selectedDesk.x + dx,
        y: selectedDesk.y + dy,
        editted: true,
      }
      setSelectedDesk(updatedDesk);
      setEditableDesks(prev => prev.map(d => d.id === updatedDesk.id ? updatedDesk : d));
    }
  }

  const rotateDesk = (deg: number) => {
    if (selectedDesk) {
      const updatedDesk = {
        ...selectedDesk,
        rotation: (selectedDesk.rotation ?? 0) + deg,
        editted: true,
      }
      setSelectedDesk(updatedDesk);
      setEditableDesks(prev => prev.map(d => d.id === updatedDesk.id ? updatedDesk : d));
    }
  }

  const renameDesk = () => {
    if (selectedDesk) {
      setRenameDeskEnabled(true);
      setEditedDeskName(selectedDesk.label);
    }
  }

  const updateDeskName = (label: string) => {
    if (selectedDesk) {
      setEditedDeskName('');
      const updatedDesk = {
        ...selectedDesk,
        editted: true,
        label: label
      }
      setSelectedDesk(updatedDesk);
      setEditableDesks(prev => prev.map(d => d.id === updatedDesk.id ? updatedDesk : d));
    }
  }

  const onMouseMove = (e: any) => {
    const nowX = e.clientX;
    const nowY = e.clientY;
    const diffX = nowX - lastMousePos.current.x;
    const diffY = nowY - lastMousePos.current.y;
    if (lockedDesk) {
      e.stopPropagation();
      const updatedDesk = {
        ...lockedDesk,
        x: lockedDesk.x + (diffX / mapScale()),
        y: lockedDesk.y + (diffY / mapScale()),
        editted: true,
      }
      setLockedDesk(updatedDesk);
      setEditableDesks(prev => prev.map(d => d.id === updatedDesk.id ? updatedDesk : d));
    }
    if (selectedMeetingRoom) {
      e.stopPropagation();
      const updatedRoom = {
        ...selectedMeetingRoom,
        x: (selectedMeetingRoom?.x ?? 0) + (diffX / mapScale()),
        y: (selectedMeetingRoom?.y ?? 0) + (diffY / mapScale()),
        moved: true,
      }
      setSelectedMeetingRoom(updatedRoom);
      setEditableMeetingRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
    }
    if (draggingArea) {
      e.stopPropagation();
      const updatedArea = {
        ...draggingArea,
        areaX: (draggingArea.areaX ?? 0) + (diffX / mapScale()),
        areaY: (draggingArea.areaY ?? 0) + (diffY / mapScale()),
        dirty: true,
      }
      setDraggingArea(updatedArea);
      setEditableAreas(prev => prev.map(a => a.id === updatedArea.id ? updatedArea : a));
    }
    lastMousePos.current = {x: nowX, y: nowY};
  }

  const deleteDeskClick = () => {
    if (selectedDesk) {
      setLoading(true);
      deleteDeskRequest(selectedDesk.id)
        .then(() => {
          setEditableDesks(prev => prev.filter(d => d.id !== selectedDesk.id));
          setSelectedDesk(undefined);
          clearSelection();
        })
        .catch(() => dispatch(failureNotification(t('floor-plan-editor.save-failed'))))
        .finally(() => setLoading(false));
    }
  }

  const saveAll = async () => {
    setLoading(true);
    try {
      const edittedDesks = editableDesks.filter(ed => ed.editted && ed.id);
      const movedRooms = editableMeetingRooms.filter(r => r.moved);
      const resizedRooms = editableMeetingRooms.filter(r => r.resized);
      const dirtyAreas = editableAreas.filter(a => a.dirty);
      await Promise.all([
        ...edittedDesks.map(ed => updateDesk(ed)),
        ...movedRooms.map(r => updateMeetingRoomPosition(r.id, r?.x ?? 0, r?.y ?? 0)),
        ...resizedRooms.map(r => updateMeetingRoomSize(r.id, r?.width ?? 0, r?.height ?? 0)),
        ...dirtyAreas.map(a => updateSingleOfficeEntity(a)),
      ]);
      setEditableDesks(prev => prev.map(d => ({...d, editted: false})));
      setEditableMeetingRooms(prev => prev.map(r => ({...r, moved: false, resized: false})));
      setEditableAreas(prev => prev.map(a => ({...a, dirty: false})));
      if (dirtyAreas.length > 0) {
        dispatch(fetchOfficeEntities());
      }
      dispatch(successNotification(t('floor-plan-editor.save-success')));
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.save-failed')));
    } finally {
      setLoading(false);
    }
  }

  const onMeetingRoomMouseDown = (e: any, room: EditableMeetingRoom) => {
    e.stopPropagation();
    setSelectedMeetingRoom(room);
  }

  const onMeetingRoomMouseRelease = (e: any, room: EditableMeetingRoom) => {
    setSelectedMeetingRoom(undefined);
  }

  const onMeetingRoomResize = (room: MeetingRoom, width: number, height: number) => {
    setEditableMeetingRooms(prev => prev.map(r => r.id === room.id ? {...r, width, height, resized: true} : r));
  }

  const onAreaMouseDown = (e: any, area: EditableAreaEntity) => {
    e.stopPropagation();
    setDraggingArea(area);
  }

  const onAreaMouseRelease = (e: any, area: EditableAreaEntity) => {
    setDraggingArea(undefined);
  }

  const onAreaResize = (area: EditableAreaEntity, width: number, height: number) => {
    setEditableAreas(prev => prev.map(a => a.id === area.id ? {...a, areaWidth: width, areaHeight: height, dirty: true} : a));
  }

  const onMouseUp = (e: any) => {
    setSelectedMeetingRoom(undefined);
    setDraggingArea(undefined);
    setLockedDesk(undefined);
  }

  if (!canEdit) {
    return (
      <AccessRequiredContainer className="management">
        <AccessRequired companyAdminsRequired={true} visible={true} />
      </AccessRequiredContainer>
    );
  }

  if (!officeIdParam || (office && (!office.floorPlanEnabled || !hasPlan))) {
    return (
      <AccessRequiredContainer>
        <BodyRegular>{t('floor-plan-editor.no-floor-plan')}</BodyRegular>
      </AccessRequiredContainer>
    );
  }

  return (
    <>
      {loading && <LoadingSpinner fullScreen={true} />}
      <Prompt when={hasUnsavedChanges} message={t('floor-plan-editor.unsaved-changes')} />
      <FloorMapContainer onMouseMove={onMouseMove} onMouseUp={onMouseUp}>

        <TransformWrapper centerOnInit={plan?.centerOnInit ?? true}
                          initialScale={plan?.initialScale ?? 0.5}
                          maxScale={plan?.maxScale ?? 2}
                          minScale={plan?.minScale ?? 0.5}
                          ref={mapRef}
                          panning={{disabled: !!lockedDesk || !!selectedMeetingRoom || !!draggingArea}}>
          {(params: any) => (
            <div style={{position: 'relative', height: '100%', width: '100%'}} onClick={clearSelection}>
              <ButtonRow>
                <ToggleableOutlineButton click={() => addDesk()} text="floor-plan-editor.add-desk" isActive={true}/>
                <ToggleableOutlineButton click={() => addArea()} text="floor-plan-editor.add-area" isActive={true}/>
                <ToggleableOutlineButton click={() => saveAll()} text="floor-plan-editor.save" isActive={true}/>
                {selectedDesk && <DeskControls>

                  <BodyRegular weight={600}>{t('floor-plan-editor.selected', {label: selectedDesk?.label})}</BodyRegular>
                  <KeyboardHint>{t('floor-plan-editor.keyboard-hint')}</KeyboardHint>
                  <ToggleableOutlineButton click={() => renameDesk()} text="floor-plan-editor.rename-desk" isActive={true}/>

                  {PORTRAIT_DESK_PRESETS.map((preset, key) => (
                    <ToggleableOutlineButton key={`portrait-${key}`}
                                             click={() => changeDeskSize(preset.width, preset.height)}
                                             text={t(preset.labelKey, {width: preset.width, height: preset.height})}
                                             isActive={true}/>
                  ))}

                  {LANDSCAPE_DESK_PRESETS.map((preset, key) => (
                    <ToggleableOutlineButton key={`landscape-${key}`}
                                             click={() => changeDeskSize(preset.width, preset.height)}
                                             text={t(preset.labelKey, {width: preset.width, height: preset.height})}
                                             isActive={true}/>
                  ))}

                  <div className={'deskControls__buttonRow'}>
                    <ToggleableOutlineButton text={t('floor-plan-editor.rotate-left', {degrees: 1})} click={() => rotateDesk(-1)} isActive={true} />
                    <ToggleableOutlineButton text={t('floor-plan-editor.rotate-right', {degrees: 1})} click={() => rotateDesk(1)} isActive={true} />
                  </div>

                  <div className={'deskControls__buttonRow'}>
                    <ToggleableOutlineButton text={t('floor-plan-editor.rotate-left', {degrees: 22.5})} click={() => rotateDesk(-22.5)} isActive={true} />
                    <ToggleableOutlineButton text={t('floor-plan-editor.rotate-right', {degrees: 22.5})} click={() => rotateDesk(22.5)} isActive={true} />
                  </div>

                  <ToggleableOutlineButton click={() => cloneDesk()} text="floor-plan-editor.clone-desk" isActive={true}/>

                  <ToggleableOutlineButton click={() => deleteDeskClick()} text="floor-plan-editor.delete-desk" isActive={true}/>

                  {renameDeskEnabled && editedDeskName && <Row style={{justifyContent: 'center'}}>
                    <TextField onChange={(val) => setEditedDeskName(val)} value={editedDeskName} />
                    <Icon className={"saveIcon"} icon={IconTypes.Tick} size={"small"} onClick={() => updateDeskName(editedDeskName)} />
                  </Row>}
                </DeskControls>}
                <MapControlsContainer className="tools">
                  <MapControlButton onClick={() => params.zoomIn()}>+</MapControlButton>
                  <MapControlButton onClick={() => params.zoomOut()}>-</MapControlButton>
                  <MapControlButton onClick={() => params.resetTransform()}><BodyVerySmall style={{fontSize: 8}} weight={600}>100%</BodyVerySmall></MapControlButton>
                </MapControlsContainer>
              </ButtonRow>

              <TransformComponent wrapperStyle={{ maxWidth: "100%", maxHeight: "100%" }}>
                {(plan || office?.officeFloorPlan) && <>
                  {office?.officeFloorPlan ?
                    <FloorPlan src={`data:image/svg+xml,${encodeURIComponent(office?.officeFloorPlan)}`} /> :
                    <FloorPlan src={getFloorPlanUrl(plan.floorPlan)} />}
                </>}

                {editableAreas.map((area: EditableAreaEntity) => (
                  <EditableAreaSpot key={area.id}
                                    area={area}
                                    onMouseDown={onAreaMouseDown}
                                    onMouseUp={onAreaMouseRelease}
                                    onResize={onAreaResize}
                                    mapScale={mapScale()} />
                ))}

                {editableDesks.length > 0 && editableDesks.map((desk: EditableDesk) => (
                  <EditableDeskSpot key={desk.id}
                                    onClick={onEditDeskClick}
                                    selected={lockedDesk?.id === desk.id || selectedDesk?.id === desk.id}
                                    onMouseDown={onDeskLock}
                                    onMouseUp={onDeskRelease}
                                    desk={desk}/>
                ))}

                {editableMeetingRooms.map((meetingRoom: EditableMeetingRoom) => <EditableMeetingRoomSpot key={meetingRoom.id}
                                                                                                         currentDate={moment()}
                                                                                                         meetingRoom={meetingRoom}
                                                                                                         onMouseDown={onMeetingRoomMouseDown}
                                                                                                         onMouseUp={onMeetingRoomMouseRelease}
                                                                                                         onResize={onMeetingRoomResize}
                                                                                                         mapScale={mapScale()} />)}

              </TransformComponent>
            </div>
          )}
        </TransformWrapper>

      </FloorMapContainer>
    </>
  )
}

interface Props {
}

const FloorMapContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background-color: ${Colours.veryLightGrey};
  height: 100vh;
  max-height: 100vh;
`

const AccessRequiredContainer = styled.div`
  padding: 48px 24px;
`

const ButtonRow = styled.div`
  display: flex;
  position: absolute;
  z-index: 9;
  padding: 8px 16px;
  width: 100%;
`

const MapControlsContainer = styled.div`
  display: flex;
  margin-left: auto;
`

const DeskControls = styled.div`
  position: absolute;
  left: 16px;
  top: 100px;
  width: 240px;
  display: flex;
  flex-direction: column;
  background-color: rgba(0, 0, 0, 0.1);
  padding: 8px;
  border-radius: 8px;
  border: 1px solid black;
  .toggleableOutlineButton {
    margin-bottom: 8px;
    width: 100%;
    button {
      max-width: unset !important;
    }
  }
  .saveIcon {
    background-color: white !important;
    border-radius: 100% !important;
    border: solid 1px ${Colours.green} !important;
    padding: 8px !important;
  }
  .textField {
    margin: 0 8px 0 0;
  }
  .deskControls__buttonRow {
    display: flex;
    justify-content: space-between;

    .toggleableOutlineButton {
      margin-right: 0;
      width: 100px;
    }
    .toggleableOutlineButton button {
      min-width: unset !important;
    }
  }
`


const KeyboardHint = styled(BodyVerySmall)`
  margin: 4px 0 8px;
`

const MapControlButton = styled.div`
  border-radius: 50%;
  height: 48px;
  width: 48px;
  background-color: white;
  border: solid 1px ${Colours.mildGrey};
  margin-left: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  span {
    color: ${Colours.mildGrey};
  }
`

const FloorPlan = styled.img`
`
